
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, Eye, User, Shield } from 'lucide-react';
import { UserReport } from '@/hooks/useContentModeration';
import { toast } from 'sonner';

interface UserReportsTabProps {
  reports: UserReport[];
  onUpdateReport: (id: string, updates: Partial<UserReport>) => Promise<void>;
}

const UserReportsTab = ({ reports, onUpdateReport }: UserReportsTabProps) => {
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const [resolution, setResolution] = useState('');
  const [processing, setProcessing] = useState<string | null>(null);

  const handleStatusUpdate = async (reportId: string, status: string, priority?: string) => {
    setProcessing(reportId);
    try {
      const updates: Partial<UserReport> = { 
        status: status as UserReport['status'],
        ...(priority && { priority: priority as UserReport['priority'] }),
        ...(resolution && { resolution })
      };
      
      await onUpdateReport(reportId, updates);
      toast.success('Report updated successfully');
      setSelectedReport(null);
      setResolution('');
    } catch (error) {
      toast.error('Failed to update report');
    } finally {
      setProcessing(null);
    }
  };

  const getPriorityBadge = (priority: string) => {
    const variants = {
      low: 'bg-blue-100 text-blue-800 border-blue-200',
      normal: 'bg-gray-100 text-gray-800 border-gray-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      urgent: 'bg-red-100 text-red-800 border-red-200'
    };
    return variants[priority as keyof typeof variants] || variants.normal;
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      investigating: 'bg-blue-100 text-blue-800 border-blue-200',
      resolved: 'bg-green-100 text-green-800 border-green-200',
      dismissed: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return variants[status as keyof typeof variants] || variants.pending;
  };

  const pendingReports = reports.filter(report => ['pending', 'investigating'].includes(report.status));
  const resolvedReports = reports.filter(report => ['resolved', 'dismissed'].includes(report.status));

  return (
    <div className="space-y-6">
      {/* Active Reports */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Active User Reports ({pendingReports.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {pendingReports.length === 0 ? (
            <p className="text-gray-400">No active user reports</p>
          ) : (
            pendingReports.map((report) => (
              <div key={report.id} className="bg-gray-700 rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-white font-medium">Reported User: {report.reported_user_id}</span>
                      <Badge className={getStatusBadge(report.status)}>
                        {report.status}
                      </Badge>
                      <Badge className={getPriorityBadge(report.priority)}>
                        {report.priority}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-300">
                      <p><strong>Report Type:</strong> {report.report_type}</p>
                      <p><strong>Reported By:</strong> {report.reported_by}</p>
                      <p><strong>Description:</strong> {report.description}</p>
                      <p><strong>Created:</strong> {new Date(report.created_at).toLocaleString()}</p>
                      {report.evidence && (
                        <p><strong>Evidence:</strong> {JSON.stringify(report.evidence)}</p>
                      )}
                    </div>
                  </div>
                </div>

                {selectedReport === report.id && (
                  <div className="space-y-3 border-t border-gray-600 pt-3">
                    <div className="grid grid-cols-2 gap-3">
                      <Select onValueChange={(value) => handleStatusUpdate(report.id, value)}>
                        <SelectTrigger className="bg-gray-600 border-gray-500">
                          <SelectValue placeholder="Update Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="investigating">Start Investigation</SelectItem>
                          <SelectItem value="resolved">Mark Resolved</SelectItem>
                          <SelectItem value="dismissed">Dismiss Report</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      <Select onValueChange={(value) => handleStatusUpdate(report.id, report.status, value)}>
                        <SelectTrigger className="bg-gray-600 border-gray-500">
                          <SelectValue placeholder="Update Priority" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low Priority</SelectItem>
                          <SelectItem value="normal">Normal Priority</SelectItem>
                          <SelectItem value="high">High Priority</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <Textarea
                      placeholder="Add resolution notes"
                      value={resolution}
                      onChange={(e) => setResolution(e.target.value)}
                      className="bg-gray-600 border-gray-500 text-white"
                    />
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleStatusUpdate(report.id, 'resolved')}
                        disabled={processing === report.id}
                        className="bg-green-600 hover:bg-green-700"
                        size="sm"
                      >
                        <Shield className="h-4 w-4 mr-1" />
                        Resolve
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedReport(null);
                          setResolution('');
                        }}
                        variant="outline"
                        size="sm"
                        className="border-gray-600 text-gray-300"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {selectedReport !== report.id && (
                  <Button
                    onClick={() => setSelectedReport(report.id)}
                    variant="outline"
                    size="sm"
                    className="border-gray-600 text-gray-300 hover:bg-gray-600"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Investigate
                  </Button>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Resolved Reports */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">Recent Resolved Reports</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {resolvedReports.slice(0, 10).map((report) => (
            <div key={report.id} className="bg-gray-700 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm">User: {report.reported_user_id}</span>
                    <Badge className={getStatusBadge(report.status)}>
                      {report.status}
                    </Badge>
                    <Badge className={getPriorityBadge(report.priority)}>
                      {report.priority}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-400">
                    Type: {report.report_type} • {new Date(report.created_at).toLocaleDateString()}
                  </p>
                  {report.resolution && (
                    <p className="text-xs text-gray-300">Resolution: {report.resolution}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserReportsTab;
